import { CSSProperties, useRef } from "react"
import { InputNumber, Space } from "antd"
import { Duration, durationToHours, durationToMinutes, incrementToHourStep, incrementToMinuteStep, stepMinuteWithRollover } from "./durationModel"

export type Props = {
  value?: Duration
  onChange?: (value: Duration) => void
  increment?: number
  disabled?: boolean
  style?: CSSProperties
}

const DurationPicker = ({ value, onChange, increment = 60, disabled, style }: Props) => {
  const effectiveValue = value ?? { seconds: 0 }
  const hours = durationToHours(effectiveValue)
  const minutes = durationToMinutes(effectiveValue)

  const hourStep = incrementToHourStep(increment)
  const minuteStep = incrementToMinuteStep(increment)

  const minuteJustStepped = useRef(false)

  const update = (newSeconds: number) => {
    onChange?.({ seconds: Math.max(0, newSeconds) })
  }

  return (
    <Space.Compact style={style}>
      <InputNumber
        value={hours}
        min={0}
        step={Math.max(1, hourStep)}
        disabled={disabled}
        onChange={(val) => {
          if (val != null) {
            update(val * 3600 + minutes * 60)
          }
        }}
        style={{ width: 60 }}
        controls
      />
      <Space.Addon>H</Space.Addon>
      <InputNumber
        value={minutes}
        step={1}
        disabled={disabled}
        onChange={(val) => {
          if (minuteJustStepped.current) {
            minuteJustStepped.current = false
            return
          }
          if (val != null) {
            const clampedMinutes = Math.min(59, Math.max(0, val))
            update(hours * 3600 + clampedMinutes * 60)
          }
        }}
        onStep={(_v, info) => {
          minuteJustStepped.current = true
          const direction: 1 | -1 = info.type === "up" ? 1 : -1
          const stepped = stepMinuteWithRollover(
            effectiveValue,
            Math.max(1, minuteStep || 1),
            direction
          )
          update(stepped.seconds)
        }}
        style={{ width: 60 }}
        controls
      />
      <Space.Addon>M</Space.Addon>
    </Space.Compact>
  )
}

export default DurationPicker
